import {
  formatFormulaNumberValue,
  formatMoneyValue,
  formatPercentageValue,
  parseDollarStrToNumber,
  parseFormulaNumberValue,
} from "../functions/permitFormHelpers";

const hasExplicitFormulaFormat = (formulaFormat) =>
  ["general", "number", "dollar", "money", "percentage"].includes(
    formulaFormat?.type,
  );

const toFormulaNumber = (value, formulaFormat) => {
  if (hasExplicitFormulaFormat(formulaFormat)) {
    return parseFormulaNumberValue(value) ?? 0;
  }

  // Preserve legacy parsing for formulas without an explicit result format.
  const parsed = parseDollarStrToNumber(value);
  const number = Number.parseFloat(parsed ?? 0);

  return Number.isFinite(number) ? number : 0;
};

const getFormulaStepValue = (formulaField, valuesByName, formulaFormat) => {
  const fieldName = formulaField?.field?.name;
  const value = fieldName ? valuesByName?.[fieldName] : formulaField?.value;

  return toFormulaNumber(value, formulaFormat);
};

export const evaluateFormulaFields = (
  formulaFields,
  valuesByName = {},
  formulaFormat,
) => {
  if (!Array.isArray(formulaFields) || formulaFields.length === 0) {
    return undefined;
  }

  let result = getFormulaStepValue(
    formulaFields[0],
    valuesByName,
    formulaFormat,
  );

  for (let index = 1; index < formulaFields.length; index += 1) {
    const formulaField = formulaFields[index];
    const value = getFormulaStepValue(
      formulaField,
      valuesByName,
      formulaFormat,
    );

    switch (formulaField?.operator?.value) {
      case "+":
        result += value;
        break;
      case "-":
        result -= value;
        break;
      case "*":
        result *= value;
        break;
      case "/":
        if (value !== 0) result /= value;
        break;
      case "=":
        result = value;
        break;
      case "%":
        result *= value / 100;
        break;
      default:
        break;
    }
  }

  return result;
};

export const formatFormulaResult = (
  result,
  usePercentage = false,
  formulaFormat,
) => {
  if (result === undefined || !Number.isFinite(result)) return undefined;

  // An explicit format takes precedence over the legacy percentage toggle.
  if (hasExplicitFormulaFormat(formulaFormat)) {
    return formatFormulaNumberValue(result, formulaFormat);
  }

  return usePercentage
    ? formatPercentageValue(result === 0 ? "0" : result)
    : formatMoneyValue(result === 0 ? "0" : result);
};

export const calculateFormulaValue = ({
  formulaFields,
  valuesByName,
  usePercentage = false,
  formulaFormat,
}) =>
  formatFormulaResult(
    evaluateFormulaFields(formulaFields, valuesByName, formulaFormat),
    usePercentage,
    formulaFormat,
  );

export const getFormulaUpdate = ({
  currentValue,
  formulaFields,
  valuesByName,
  usePercentage = false,
  formulaFormat,
}) => {
  const computedValue = calculateFormulaValue({
    formulaFields,
    valuesByName,
    usePercentage,
    formulaFormat,
  });

  return computedValue === undefined || currentValue === computedValue
    ? undefined
    : computedValue;
};
